import React, { useState, useContext } from 'react';
import { Minus, Plus, FileText, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import type { StagedProduct } from '@/stores/stagedProducts/stagedProducts.types';
import type { SelectedProductModifier } from '@/types';
import { lineUnitPrice } from '@/lib/productModifiers';
import { useCurrency } from '@/hooks/useCurrency';
import { useIvaPricing } from '@/hooks/useIvaPricing';
import { useIsTouch } from '@/hooks/use-touch';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import NumericKeyboard from '@/components/NumericKeyboard';
import { SheetPortal } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { ModifierNotesModal } from './ModifierNotesModal';
import { CustomerPositionModal } from './CustomerPositionModal';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useTabsViewMode } from '@/hooks/useTabsViewMode';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { calculateWeightLinePrice, formatWeight, hasPositiveWeight } from '@/utils/weightPricing';
import TabItemQuantityDialog from '@/components/tabs/TabItemQuantityDialog';

interface StagedProductsListProps {
  stagedProducts: StagedProduct[];
  isCommitting: boolean;
  onDecreaseQuantity: (lineId: string) => void;
  onIncreaseQuantity: (lineId: string) => void;
  onSetQuantity: (lineId: string, quantity: number) => void;
  onSetWeight: (lineId: string, weightKg: number) => void;
  onToggleNotes: (lineId: string, hasExistingNotes: boolean) => void;
  onNotesChange: (lineId: string, notes: string, modifiers?: SelectedProductModifier[]) => void;
  onSetCustomerPosition: (lineId: string, customerPosition: number | undefined) => void;
  scrollContainerClassName?: string;
}

const StagedProductsList: React.FC<StagedProductsListProps> = ({
  stagedProducts,
  isCommitting,
  onDecreaseQuantity,
  onIncreaseQuantity,
  onSetQuantity,
  onSetWeight,
  onNotesChange,
  onSetCustomerPosition,
  scrollContainerClassName,
}) => {
  const { formatAmount } = useCurrency();
  const { showIvaPrice, getPriceWithIva } = useIvaPricing();
  const isTouch = useIsTouch();
  const { translator } = useContext(LanguageProviderContext);
  const [numpadLineId, setNumpadLineId] = useState<string | null>(null);
  const [numpadValue, setNumpadValue] = useState('');
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});
  const [notesModalLineId, setNotesModalLineId] = useState<string | null>(null);
  const [weightLineId, setWeightLineId] = useState<string | null>(null);
  const [customerPositionLineId, setCustomerPositionLineId] = useState<string | null>(null);
  const { isEnabled } = useFeatureFlag();
  const { viewMode } = useTabsViewMode();
  const isCustomerPositionEnabled = isEnabled(FEATURE_FLAGS.CUSTOMER_POSITIONS) && viewMode === 'tables';
  const closeQuantityNumpad = () => {
    setNumpadLineId(null);
    setNumpadValue('');
  };

  const openNumpad = (lineId: string, currentQty: number) => {
    if (isCommitting) return;
    setCustomerPositionLineId(null);
    setWeightLineId(null);
    setNumpadValue(String(currentQty));
    setNumpadLineId(lineId);
  };

  const openWeightEditor = (lineId: string) => {
    if (isCommitting) return;
    closeQuantityNumpad();
    setCustomerPositionLineId(null);
    setWeightLineId(lineId);
  };

  const confirmNumpad = (lineId: string) => {
    const qty = Math.round(parseFloat(numpadValue) || 0);
    if (!isNaN(qty)) onSetQuantity(lineId, qty);
    closeQuantityNumpad();
  };

  const handleInputFocus = (lineId: string, currentQty: number) => {
    setLocalInputValues((prev) => ({ ...prev, [lineId]: String(currentQty) }));
  };

  const handleInputChange = (lineId: string, value: string) => {
    if (!/^\d*$/.test(value)) return;
    setLocalInputValues((prev) => ({ ...prev, [lineId]: value }));
    const qty = parseInt(value, 10);
    if (qty > 0) onSetQuantity(lineId, qty);
  };

  const handleInputBlur = (lineId: string) => {
    setLocalInputValues((prev) => {
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  };

  const handleToggleNotes = (lineId: string) => {
    setNotesModalLineId(lineId);
  };

  const handleToggleCustomerPosition = (lineId: string) => {
    if (isCommitting) return;
    closeQuantityNumpad();
    setCustomerPositionLineId(lineId);
  };

  if (stagedProducts.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-muted-foreground">
        {translator('ProductsCatalog.emptyStagedItems')}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'space-y-3 overflow-y-auto overscroll-contain touch-pan-y',
        scrollContainerClassName ?? 'max-h-[437px]',
      )}
    >
      {stagedProducts.map(({ lineId, product, quantity, notes = '', customerPosition, modifiers, weightKg }) => {
        const hasModifiers = Boolean(modifiers?.length);
        const hasNotes = notes.trim().length > 0 || hasModifiers;
        const hasCustomerPosition = isCustomerPositionEnabled && customerPosition != null;
        const isWeight = hasPositiveWeight(weightKg);
        const displayPrice = isWeight
          ? calculateWeightLinePrice(product.price, weightKg)
          : lineUnitPrice(product, modifiers);

        return (
          <React.Fragment key={lineId}>
            <div className="rounded-md border p-2 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-1">
                  <p className="font-medium leading-tight">{product.name}</p>
                  <div className="flex flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant="outline">{formatAmount(displayPrice)}</Badge>
                      {isWeight && (
                        <Badge variant="secondary">{formatWeight(weightKg)}</Badge>
                      )}
                    </div>
                    {showIvaPrice && (
                      <span className="text-[10px] leading-tight text-muted-foreground">
                        {formatAmount(getPriceWithIva(displayPrice))} {translator('Global.ivaIncluded')}
                      </span>
                    )}
                  </div>
                  {hasModifiers && (
                    <p className="text-xs text-muted-foreground">
                      {modifiers.map((modifier) => `+ ${modifier.name}`).join(' · ')}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {isWeight ? (
                    <div className="relative flex items-center gap-1 rounded-md border">
                      <span className="h-8 w-8 shrink-0" aria-hidden />
                      <span className={cn("shrink-0", isTouch ? "w-8" : "w-12")} aria-hidden />
                      <span className="h-8 w-8 shrink-0" aria-hidden />
                      <Button
                        type="button"
                        variant="ghost"
                        className="absolute inset-0 h-auto rounded-md px-2 text-sm font-medium tabular-nums"
                        onClick={() => openWeightEditor(lineId)}
                        disabled={isCommitting}
                        aria-label={translator('Tabs.adjustWeight')}
                      >
                        {formatWeight(weightKg)}
                      </Button>
                    </div>
                  ) : (
                  <div className="flex items-center gap-1 rounded-md border">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDecreaseQuantity(lineId)}
                      disabled={isCommitting}
                      className="h-8 w-8"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    {isTouch ? (
                      <span
                        className="w-8 text-center text-sm font-medium cursor-pointer select-none"
                        onClick={() => openNumpad(lineId, quantity)}
                      >
                        {quantity}
                      </span>
                    ) : (
                      <Input
                        type="text"
                        inputMode="numeric"
                        className="w-12 h-7 text-center text-sm font-medium px-1 py-0.5"
                        value={lineId in localInputValues ? localInputValues[lineId] : String(quantity)}
                        min="1"
                        hideSpinners
                        showcustomNumPadKeyboard={false}
                        disabled={isCommitting}
                        onFocus={() => handleInputFocus(lineId, quantity)}
                        onChange={(e) => handleInputChange(lineId, e.target.value)}
                        onBlur={() => handleInputBlur(lineId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          } else if (e.key === 'Escape') {
                            setLocalInputValues((prev) => {
                              const next = { ...prev };
                              delete next[lineId];
                              return next;
                            });
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onIncreaseQuantity(lineId)}
                      disabled={isCommitting}
                      className="h-8 w-8"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  )}
                  <div className="flex items-center gap-1">
                    {isCustomerPositionEnabled && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleCustomerPosition(lineId)}
                        disabled={isCommitting}
                        className="h-8 w-8 relative"
                        aria-label={translator('ProductsCatalog.customerPosition')}
                      >
                        <User className="h-4 w-4" />
                        {hasCustomerPosition && (
                          <div className="absolute -top-0 right-0.5 h-2 w-2 rounded-full bg-red-500 border border-background" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleNotes(lineId)}
                      disabled={isCommitting}
                      className="h-8 w-8 relative"
                    >
                      <FileText className="h-4 w-4" />
                      {hasNotes && (
                        <div className="absolute -top-0 right-0.5 h-2 w-2 rounded-full bg-red-500 border border-background" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {isTouch && numpadLineId === lineId && (
              <SheetPortal>
                <div className="fixed inset-0 z-50 pointer-events-none">
                  <div className="absolute bottom-20 right-0 w-80 h-auto max-h-[60vh] p-0 flex flex-col border-t border-l rounded-tl-lg bg-background shadow-lg pointer-events-auto">
                    <NumericKeyboard
                      value={numpadValue}
                      onChange={setNumpadValue}
                      onConfirm={() => confirmNumpad(lineId)}
                      onCancel={closeQuantityNumpad}
                      translator={translator}
                    />
                  </div>
                </div>
              </SheetPortal>
            )}
          </React.Fragment>
        );
      })}
      <CustomerPositionModal
        isOpen={customerPositionLineId !== null}
        onOpenChange={(open) => !open && setCustomerPositionLineId(null)}
        stagedProduct={stagedProducts.find((p) => p.lineId === customerPositionLineId) || null}
        onSave={onSetCustomerPosition}
      />
      <TabItemQuantityDialog
        key={`${weightLineId}-${stagedProducts.find((p) => p.lineId === weightLineId)?.weightKg ?? ''}`}
        isOpen={weightLineId !== null}
        onClose={() => setWeightLineId(null)}
        currentQuantity={stagedProducts.find((p) => p.lineId === weightLineId)?.weightKg ?? 0}
        productName={stagedProducts.find((p) => p.lineId === weightLineId)?.product.name ?? ''}
        isSoldByWeight
        onConfirm={(weightKg) => {
          if (!weightLineId) return;
          onSetWeight(weightLineId, weightKg);
        }}
      />
      <ModifierNotesModal
        isOpen={notesModalLineId !== null}
        onOpenChange={(open) => !open && setNotesModalLineId(null)}
        stagedProduct={stagedProducts.find((p) => p.lineId === notesModalLineId) || null}
        onSave={onNotesChange}
      />
    </div>
  );
};

export default StagedProductsList;
